// First-person ADS (James, 2026-10-23): holding L2 pushes the lens into the cowboy's eye and the gun
// comes up into the sights, with both armoured gauntlets on it. The gun is the same model, batch and
// action state as the one on the cowboy (GunRack.drawAt), so its slides, cylinders, bolts, drums and
// barrels keep working; this file only decides WHERE it sits and how it moves in front of the eye.
//
// Benchmarks (what the motion is chasing): Call of Duty's ADS raise (the gun swings up from low right
// and settles with one small overshoot), its look sway (the gun trails the turn and springs back), the
// tight ADS walk bob and the kick that throws the gun back and up and snaps home. Not a simulator:
// reloads are the gun's own choreography (render/cowboys/holds.ts reloadFrame) brought up close, and a
// few guns finish theirs with a flourish (the Big Iron's spin, the break-actions' flick shut).
//
// Frames: camera space is x right, y up, looking down -Z. A gun's frame (gunkit.ts) has the barrel
// down +Z and top +Y, so it is turned half a turn about Y to point away from the eye: the gun's -X
// flank faces right. The gauntlets are built in the gun frame around the grip (right hand) and the
// foregrip (left hand), and the forearms are posed from the wrists back to elbows below the lens.
//
// Draws: the gun rides its existing batch (no extra draw); the gauntlets are one instanced batch
// (one draw + shadow). Allocation-free per frame.

import * as THREE from 'three';
import { PartBuilder, Rig, TypeBatch, Fx, T } from '../render/robots/kit';
import { PAL } from '../render/palette';
import { W, WEAPONS } from '../../sim/weapons';
import type { World, RayHit } from '../../sim/world';
import { feelOf } from '../feel/recoil';
import { holdOf, reloadFrame, ReloadK, HoldK } from '../render/cowboys/holds';
import { buildGunModel } from './gunmodels';
import { C, paintFor, rb, rod, cz, cy, ringZ, ball, lathe, kick, smooth, clamp01, type GunAct } from './gunkit';
import { gunMat, type GunRack } from './guns';

/** the gun frame's right-hand flank (see the frame note above) */
const RS = -1;
const TAU = Math.PI * 2;

// ---------------------------------------------------------------- per-gun fit
// Where the eye looks along each gun: sight height above the grip (m) and the grip's distance in
// front of the eye. Sights are measured off each model (the top of its rear half); `sight` overrides
// the measure where the eye should sit lower (a scope's centre, a tube's top edge).
interface Fit { sight?: number; dist: number; x?: number; lift?: number; yaw?: number; roll?: number }
const FIT: Partial<Record<number, Fit>> = {
  [W.Starter]: { dist: 0.3, yaw: 0.08, roll: -0.1 },
  [W.BigIron]: { dist: 0.32, yaw: 0.1, roll: -0.12 },
  [W.DuelBlaster]: { dist: 0.3, yaw: 0.08, roll: -0.1 },
  [W.Akimbo]: { dist: 0.32 },
  [W.Scoundrel]: { dist: 0.3 },
  [W.PulseRifle]: { dist: 0.3, lift: 0.012 },
  [W.Gentlemen]: { dist: 0.31 },
  [W.BoltSniper]: { dist: 0.3, lift: 0.014, x: -0.02 },
  // the Auto MG rides low and to the right: you look past its feed cover, not through it
  [W.AutoMG]: { dist: 0.52, x: 0.15, lift: 0.08 },
  [W.Harpoon]: { dist: 0.3 },
};
const fitFor = (wid: number): Fit => {
  const f = FIT[wid];
  if (f) return f;
  const k = holdOf(wid, false).k;
  if (k === HoldK.Launcher) return { dist: 0.3, x: 0.21, lift: 0.04 };
  if (k === HoldK.Lob) return { dist: 0.42 };
  if (k === HoldK.Heave) return { dist: 0.42, x: 0.04 };
  return { dist: 0.32 };
};

/** measured sight height (top of the gun over its rear half, near the centre line), cached per gun */
const sightCache = new Map<number, number>();
function sightOf(wid: number): number {
  const fit = fitFor(wid);
  if (fit.sight !== undefined) return fit.sight;
  let s = sightCache.get(wid);
  if (s !== undefined) return s;
  const m = buildGunModel(wid, paintFor(0));
  const g = m.rig.build(false);
  const p = g.attributes.position.array as Float32Array;
  const zMax = Math.max(0.08, m.muzzle.z * 0.55);
  let top = 0.03;
  for (let i = 0; i < p.length; i += 3) {
    const x = p[i], y = p[i + 1], z = p[i + 2];
    if (Math.abs(x) < 0.012 && z > -0.06 && z < zMax && y > top && y < 0.25) top = y;
  }
  g.dispose();
  s = top + 0.006 + (fit.lift ?? 0);
  sightCache.set(wid, s);
  return s;
}

// ---------------------------------------------------------------- the gauntlets
// From the gunslinger sheet (art_refs/gunslinger.png): gunmetal plate over dark leather, brass
// rivets and bands, a steel cuff, a class-coloured stripe down the bracer.
const LEATHER = 0x4a3426, LEATHER_DK = 0x2a1f1a, STRAP = 0x6b4a2e, PLATE = 0x57514c, PLATE_LT = 0x9a9183;
/** per class: the stripe on the bracer (muted, printed) */
const CLASS_INK = [0xc9893a, 0xb4532a, 0x7a5aa8, 0x4f8f8c];

const enum S { RPalm, RFingers, RIndex, LPalm, LFingers, LCup, LCupFingers, RArm, LArm, LTwin, LTwinIndex, N }

type Add = (g: THREE.BufferGeometry, col: number, o?: { outline?: boolean; rust?: number; chip?: number; jitter?: number; glow?: number }) => void;
function part(fn: (add: Add) => void, mirror = false): THREE.BufferGeometry {
  const pb = new PartBuilder(0.0022);
  fn((g, col, o = {}) => { pb.add(g, col, { rust: 0.06, chip: 0.6, outline: true, jitter: 0.05, ...o }); });
  const g = pb.build();
  return mirror ? mirrorX(g) : g;
}
/** mirror a built part across X (positions and normals flip, triangles rewind so they still face out) */
function mirrorX(g: THREE.BufferGeometry) {
  const names = Object.keys(g.attributes);
  for (const name of names) {
    const at = g.attributes[name], a = at.array as Float32Array, w = at.itemSize;
    if (name === 'position' || name === 'normal') for (let i = 0; i < a.length; i += 3) a[i] = -a[i];
    for (let t = 0; t < a.length; t += w * 3) for (let k = 0; k < w; k++) { const v = a[t + w + k]; a[t + w + k] = a[t + 2 * w + k]; a[t + 2 * w + k] = v; }
  }
  return g;
}
/** a finger: a bent rod through the points, a plate on its first knuckle */
function finger(add: Add, pts: [number, number, number][], r: number, plate = true) {
  for (let i = 0; i + 1 < pts.length; i++) {
    add(rod(pts[i], pts[i + 1], r * (1 - i * 0.08), 7, r * (1 - (i + 1) * 0.08)), LEATHER, { outline: true });
    add(ball(r * (1 - (i + 1) * 0.08) * 1.02, ...pts[i + 1], 7, 5), LEATHER, { outline: false });
  }
  if (plate && pts.length > 2) {
    const [b, c] = [pts[1], pts[2]];
    add(rod([b[0] + (c[0] - b[0]) * 0.2, b[1] + r * 0.5, b[2] + (c[2] - b[2]) * 0.2], [b[0] + (c[0] - b[0]) * 0.75, b[1] + r * 0.5, b[2] + (c[2] - b[2]) * 0.75], r * 0.6, 6), PLATE_LT, { outline: false, chip: 1 });
  }
  if (plate) {
    const [a, b] = [pts[0], pts[1]];
    add(rod([a[0] + (b[0] - a[0]) * 0.15, a[1] + r * 0.55, a[2] + (b[2] - a[2]) * 0.15], [a[0] + (b[0] - a[0]) * 0.8, a[1] + r * 0.55, a[2] + (b[2] - a[2]) * 0.8], r * 0.62, 6), PLATE, { outline: false, chip: 0.9 });
  }
}

/** right hand around the grip (gun frame, origin where the hand closes) */
function rightHand(add: Add) {
  add(rb(0.028, 0.088, 0.072, 0.012, RS * 0.026, -0.03, -0.008), LEATHER);
  // the back of the hand: three overlapping plates stepping down to the knuckles, brass-edged, riveted
  for (let i = 0; i < 3; i++) {
    const z = -0.03 + i * 0.022;
    add(rb(0.011, 0.07 - i * 0.004, 0.026, 0.004, RS * (0.043 + i * 0.0015), -0.027, z, 0, 0, RS * 0.08), i === 1 ? PLATE_LT : PLATE, { chip: 1 });
    add(rb(0.0035, 0.066 - i * 0.004, 0.004, 0.0012, RS * (0.0495 + i * 0.0015), -0.027, z + 0.012), C.brass, { outline: false, chip: 0.5 });
  }
  for (const [y, z] of [[0.004, -0.03], [-0.058, -0.03]]) add(ball(0.004, RS * 0.05, y, z, 6, 4), C.brass, { outline: false });
  // the knuckle guard: plates over each knuckle and a brass bar across them
  for (let i = 0; i < 3; i++) add(rb(0.014, 0.022, 0.02, 0.005, RS * 0.031, -0.004 - i * 0.023, 0.026, 0, RS * 0.2), i === 1 ? PLATE_LT : PLATE, { chip: 0.9 });
  add(cy(0.0042, 0.07, 6, RS * 0.04, -0.027, 0.034), C.brass, { outline: true });
  // the web of the hand riding high on the back strap
  add(rb(0.03, 0.02, 0.03, 0.008, RS * 0.006, 0.022, -0.026), LEATHER);
}
function rightFingers(add: Add) {
  for (const y of [-0.004, -0.027, -0.05]) {
    const r = y < -0.04 ? 0.0078 : 0.0088;
    finger(add, [[RS * 0.032, y, 0.03], [RS * 0.012, y, 0.042], [-RS * 0.014, y, 0.034], [-RS * 0.021, y, 0.014]], r);
  }
  // the thumb over the top of the left flank
  finger(add, [[RS * 0.014, 0.02, -0.03], [-RS * 0.016, 0.03, -0.014], [-RS * 0.024, 0.031, 0.012]], 0.0098);
}
/** the trigger finger, built from its knuckle (posed about it) */
function rightIndex(add: Add) {
  finger(add, [[0, 0, 0], [-RS * 0.018, 0.002, 0.02], [-RS * 0.03, -0.002, 0.026]], 0.0088);
}
const INDEX_KNUCKLE: [number, number, number] = [RS * 0.032, 0.02, 0.028];

/** left hand under a foregrip (origin under the barrel at the grip point): palm up, fingers over the right flank */
function leftPalm(add: Add) {
  add(rb(0.058, 0.024, 0.082, 0.01, -RS * 0.006, -0.034, 0), LEATHER);
  add(rb(0.012, 0.05, 0.064, 0.004, -RS * 0.034, -0.03, 0, 0, 0, RS * 0.5), PLATE, { chip: 0.9 });
  for (const z of [-0.022, 0.022]) add(ball(0.0034, -RS * 0.041, -0.034, z, 6, 4), C.brass, { outline: false });
}
function leftFingers(add: Add) {
  for (const dz of [-0.022, 0, 0.022]) finger(add, [[RS * 0.022, -0.04, dz], [RS * 0.036, -0.016, dz], [RS * 0.03, 0.008, dz], [RS * 0.016, 0.02, dz]], 0.0086, false);
  finger(add, [[-RS * 0.026, -0.036, -0.032], [-RS * 0.034, -0.006, -0.004], [-RS * 0.028, 0.008, 0.026]], 0.0096);
}
/** left hand cupping the right on a one-handed gun (gun frame, around the grip) */
function leftCup(add: Add) {
  add(rb(0.024, 0.078, 0.066, 0.01, -RS * 0.034, -0.036, 0.002), LEATHER);
  add(rb(0.01, 0.066, 0.052, 0.004, -RS * 0.049, -0.036, 0.0), PLATE, { chip: 0.9 });
  for (const y of [-0.012, -0.06]) add(ball(0.0034, -RS * 0.055, y, 0, 6, 4), C.brass, { outline: false });
}
function leftCupFingers(add: Add) {
  for (const y of [-0.014, -0.036, -0.058]) finger(add, [[-RS * 0.036, y, 0.03], [-RS * 0.012, y, 0.056], [RS * 0.022, y, 0.052], [RS * 0.04, y, 0.03]], 0.0082, false);
  finger(add, [[-RS * 0.034, -0.002, -0.022], [-RS * 0.034, 0.008, 0.008], [-RS * 0.03, 0.012, 0.036]], 0.0094);
}
/** a forearm: wrist at the origin, the bracer down +Z toward the elbow, the sleeve past it */
function forearm(add: Add, ink: number) {
  // the arm under the armour, the leather undersleeve past the elbow plate
  add(lathe([[0.024, 0], [0.027, 0.03], [0.031, 0.12], [0.034, 0.22], [0.036, 0.27]], 12), LEATHER, { rust: 0, chip: 0 });
  add(lathe([[0.038, 0.25], [0.043, 0.3], [0.047, 0.4], [0.048, 0.48]], 10), LEATHER_DK, { rust: 0, chip: 0 });
  add(T(new THREE.TorusGeometry(0.043, 0.006, 4, 12), 0, 0, 0.3), LEATHER, { outline: false, rust: 0, chip: 0 });
  // three overlapping lames down the top of the forearm, each a touch wider, stepping toward the elbow
  for (let i = 0; i < 3; i++) {
    const z = 0.05 + i * 0.068, w = 0.05 + i * 0.006;
    add(rb(w, 0.012, 0.078, 0.005, 0, 0.026 + i * 0.0028, z, -0.06), i === 1 ? PLATE_LT : PLATE, { chip: 1 });
    add(ball(0.0034, w * 0.4, 0.033 + i * 0.0028, z - 0.026, 6, 4), C.brass, { outline: false });
    add(ball(0.0034, -w * 0.4, 0.033 + i * 0.0028, z - 0.026, 6, 4), C.brass, { outline: false });
  }
  // a worn leather strap round the middle with a brass buckle; the class colour is a small enamel badge
  add(lathe([[0.04, 0.115], [0.0405, 0.13], [0.04, 0.145]], 14), STRAP, { rust: 0, chip: 0 });
  add(rb(0.016, 0.006, 0.02, 0.002, 0, 0.042, 0.13), C.brass, { outline: true, chip: 0.6 });
  add(rb(0.02, 0.004, 0.026, 0.002, 0, 0.034, 0.215, -0.06), ink, { outline: true, chip: 0.5 });
  for (const sx of [1, -1]) for (let i = 0; i < 5; i++) add(ball(0.0042, sx * 0.031, 0.018, 0.03 + i * 0.045, 6, 4), C.brass, { outline: false });
  // the underside guard and the wrist cuff
  add(rb(0.04, 0.01, 0.16, 0.004, 0, -0.03, 0.12, 0.05), PLATE, { chip: 0.9 });
  add(lathe([[0.03, -0.004], [0.036, 0.004], [0.034, 0.03], [0.031, 0.04]], 14), C.steel, { chip: 1 });
  add(ringZ(0.033, 0.005, 0.2, 0, 0, 14), C.brass, { outline: false });
  add(ringZ(0.027, 0.0045, 0.03, 0, 0, 14), C.brassLt, { outline: false });
  // a little piston up the outside, brass rod into a steel cylinder
  add(cz(0.0075, 0.0075, 0.06, 0.19, 8, 0.034, 0.004), C.steel, { outline: true });
  add(cz(0.0045, 0.0045, 0.015, 0.07, 6, 0.034, 0.004), C.brassLt, { outline: false });
  add(ball(0.0075, 0.034, 0.004, 0.012, 6, 4), C.brass, { outline: false });
}

// ---------------------------------------------------------------- the view
/** what the game tells the view model each frame */
export interface ViewIn {
  /** first person is wanted (aiming on foot or in hover, alive, not rolling or swinging) */
  on: boolean;
  /** camera fps weight 0..1 (CameraRig.fpsW): the gun shows once the lens is inside the head */
  lens: number;
  id: number; cls: number;
  /** body velocity (m/s, world) and grounded */
  vx: number; vy: number; vz: number; grounded: boolean;
  /** sniper steady 0..1 (Perk: the hold tightens the sway) */
  steady: number;
}

/** a damped spring on one value (sub-stepped so a slow frame stays stable) */
class Spring {
  x = 0; v = 0;
  constructor(public w: number, public z: number) {}
  step(target: number, dt: number) {
    const n = dt > 1 / 90 ? 3 : 1, h = dt / n, w = this.w;
    for (let i = 0; i < n; i++) { this.v += (w * w * (target - this.x) - 2 * this.z * w * this.v) * h; this.x += this.v * h; }
    return this.x;
  }
  kick(v: number) { this.v += v; }
}

const _cam = new THREE.Matrix4(), _g = new THREE.Matrix4(), _gw = new THREE.Matrix4(), _m = new THREE.Matrix4(), _m2 = new THREE.Matrix4();
const _q = new THREE.Quaternion(), _q2 = new THREE.Quaternion(), _e = new THREE.Euler(0, 0, 0, 'YXZ');
const _p = new THREE.Vector3(), _a = new THREE.Vector3(), _b = new THREE.Vector3(), _c = new THREE.Vector3(), _ONE = new THREE.Vector3(1, 1, 1);
const _sc = new THREE.Vector3(1, 1, 1);
const _fwd = new THREE.Vector3(), _rt = new THREE.Vector3(), _up = new THREE.Vector3();
const _hit: RayHit = { t: 0, kind: 0, nx: 0, ny: 0, nz: 0 };
const FLIP = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), Math.PI);
/** the held pose: the gun sits a little right of and below the eye line, its muzzle turned in to meet the
 *  crosshair a few metres out, so you look over it at the sights and still see its left flank (the HUD
 *  reticle carries the exact aim). x right, drop below the sight line, extra distance, yaw in, roll. */
/** the lens the view model is framed for (the ADS fov at the default 70) */
const VM_FOV = 56;
export const VM_TUNE = { x: 0.035, drop: 0.045, far: 0.16, yaw: 0.05, roll: -0.03, scale: 0.85 };

export class ViewModel {
  /** 0..1: the gun is up in front of the eye */
  shown = 0;
  /** true while the view model drew this frame (the game takes its muzzle for your shots) */
  active = false;
  private readonly muzzle = new THREE.Vector3();
  private readonly hands: TypeBatch[] = [];
  private fx = new Fx();
  private raise = new Spring(17, 0.62);
  private kz = new Spring(38, 0.55); private kp = new Spring(30, 0.5); private kr = new Spring(26, 0.45);
  private ky = new Spring(28, 0.5);
  private swayY = new Spring(14, 0.7); private swayP = new Spring(14, 0.7); private swayX = new Spring(12, 0.8);
  private lift = new Spring(10, 0.55); private roll = new Spring(9, 0.8);
  private tuck = new Spring(16, 0.9);
  private flour = 0; private flourK = 0;
  private bob = 0; private t = 0;
  private lastShots = -1; private lastWid = -1; private wasReload = false; private wasGround = true;
  private prevYaw = 0; private prevPitch = 0; private hand = new THREE.Vector3(); private handInit = false;
  private akSide = 0;

  constructor(scene: THREE.Object3D) {
    // one batch per class stripe, built now so the first L2 never builds or compiles anything
    for (let cls = 0; cls < 4; cls++) {
      const rig = new Rig();
      const ink = CLASS_INK[cls];
      const add = (fn: (a: Add) => void, mirror = false, shadow: 'box' | 'round' | 'none' = 'box') => rig.add(part(fn, mirror), 1, shadow);
      add(rightHand); add(rightFingers, false, 'none'); add(rightIndex, false, 'none');
      add(leftPalm); add(leftFingers, false, 'none');
      add(leftCup); add(leftCupFingers, false, 'none');
      add((a) => forearm(a, ink), false, 'round'); add((a) => forearm(a, ink), true, 'round');
      // the akimbo off-hand: the right hand mirrored onto the twin
      add((a) => { rightHand(a); rightFingers(a); }, true); add(rightIndex, true, 'none');
      const b = new TypeBatch(rig.build(true), rig.slots, 1, true, `vm-hands-${cls}`, gunMat, rig.mainCount);
      scene.add(b.mesh);
      this.hands.push(b);
    }
    const rim = new THREE.Color(PAL.sunCore);
    this.fx.rim[0] = rim.r * 0.7; this.fx.rim[1] = rim.g * 0.7; this.fx.rim[2] = rim.b * 0.7;
    this.fx.seed = 0.21;
  }

  /** your muzzle in the world while the view model is up (null otherwise) */
  muzzleOf(out: THREE.Vector3): boolean {
    if (!this.active) return false;
    out.copy(this.muzzle);
    return true;
  }

  /**
   * Pose and draw. Call after CowboyView.sync (the rack has this frame's action state) and before
   * GunRack.end; `cam` must already be placed for this frame.
   */
  frame(dt: number, cam: THREE.PerspectiveCamera, v: ViewIn, rack: GunRack, world: World | null) {
    this.t += dt;
    for (const b of this.hands) b.begin();
    const held = rack.heldOf(v.id);
    const target = v.on && held ? 1 : 0;
    // the raise: one small overshoot as it settles into the shoulder, a quicker drop on the way out
    this.raise.w = target ? 17 : 24;
    const r = this.raise.step(target, dt);
    this.shown = clamp01(r);
    this.active = !!held && v.lens > 0.35 && r > 0.02;
    if (!this.active || !held) { this.lastShots = -1; this.handInit = false; for (const b of this.hands) b.end(); return; }
    const { wid, act } = held;
    const d = WEAPONS[wid], hold = holdOf(wid, d.pistol), fit = fitFor(wid), f = feelOf(wid);
    if (wid !== this.lastWid) { this.lastWid = wid; this.lastShots = act.shots; this.flour = 0; }

    cam.updateMatrixWorld();
    _cam.copy(cam.matrixWorld);
    // a zoomed lens (the sniper's scope fov, the punch) would blow the gun up with it: scale the whole
    // view model about the eye by the zoom so it holds the same size on screen
    const zk = Math.tan((VM_FOV * Math.PI) / 360) / Math.tan((cam.fov * Math.PI) / 360);
    if (Math.abs(zk - 1) > 1e-3) _cam.multiply(_m.makeScale(zk, zk, zk));
    _fwd.set(0, 0, -1).transformDirection(_cam); _rt.set(1, 0, 0).transformDirection(_cam); _up.set(0, 1, 0).transformDirection(_cam);

    // ---- look sway: the gun trails the turn and springs back
    const yaw = Math.atan2(-_fwd.x, -_fwd.z), pitch = Math.asin(Math.max(-1, Math.min(1, _fwd.y)));
    let dy = yaw - this.prevYaw; dy = Math.atan2(Math.sin(dy), Math.cos(dy));
    const dp = pitch - this.prevPitch;
    this.prevYaw = yaw; this.prevPitch = pitch;
    const id = dt > 1e-4 ? 1 / dt : 0;
    const yr = Math.max(-6, Math.min(6, dy * id)), pr = Math.max(-6, Math.min(6, dp * id));
    const sy = this.swayY.step(-yr * 0.012, dt), sp = this.swayP.step(-pr * 0.01, dt), sx = this.swayX.step(-yr * 0.004, dt);

    // ---- body motion: ADS walk bob (tight), strafe roll, the lift in the air and the dip on landing
    const latV = v.vx * _rt.x + v.vz * _rt.z;
    const flat = Math.hypot(v.vx, v.vz);
    if (v.grounded) this.bob += flat * dt * (TAU / 1.5);
    const bobA = v.grounded ? Math.min(1, flat / 5) : 0;
    const bx = Math.sin(this.bob * 0.5) * 0.007 * bobA, by = -Math.abs(Math.cos(this.bob * 0.5)) * 0.005 * bobA + 0.0025 * bobA;
    const rl = this.roll.step(-latV * 0.012, dt);
    const lf = this.lift.step(v.grounded ? 0 : Math.max(-0.035, Math.min(0.035, -v.vy * 0.004)), dt);
    if (v.grounded && !this.wasGround) this.lift.kick(-0.28 - Math.min(0.5, Math.abs(v.vy) * 0.02));
    this.wasGround = v.grounded;
    // breathing: a slow figure of eight; the sniper's steady hold all but stills it
    const br = (d.id === W.BoltSniper ? 1.6 - 1.3 * v.steady : 0.5) * (1 - bobA * 0.6);
    const bx2 = Math.sin(this.t * 0.9) * 0.0016 * br, by2 = Math.sin(this.t * 1.8) * 0.0011 * br;

    // ---- recoil: back, up, a twist; heavy guns hit harder and settle slower
    if (this.lastShots < 0) this.lastShots = act.shots;
    if (act.shots !== this.lastShots) {
      const n = act.shots - this.lastShots;
      this.lastShots = act.shots;
      if (n > 0) {
        const pw = Math.min(2.2, f.body) * (0.7 + f.pitch * 0.12);
        const h = Math.sin(act.shots * 12.9898) * 43758.5453 % 1;
        this.kz.kick(0.9 * pw); this.kp.kick(2.6 * pw * (0.8 + f.pitch * 0.15));
        this.kr.kick((h - 0.5) * 3 * pw + RS * 0.6 * pw); this.ky.kick((h - 0.5) * 1.2 * pw);
        if (d.akimbo) this.akSide = act.shots & 1;
        if (wid === W.BoltSniper) this.flourish(2, 0.62);
      }
    }
    const kz = this.kz.step(0, dt), kp = this.kp.step(0, dt), kr = this.kr.step(0, dt), ky = this.ky.step(0, dt);

    // ---- reload: the gun's own choreography, brought close; the flourish as it finishes
    const rw = smooth(act.reload);
    if (act.reload > 0.5) this.wasReload = true;
    else if (this.wasReload) {
      this.wasReload = false;
      if (wid === W.BigIron) this.flourish(1, 0.42);
      else if (hold.reload === ReloadK.Break || hold.reload === ReloadK.Flick) this.flourish(3, 0.3);
      else this.kp.kick(1.6);
    }
    let rx = 0, ry = 0, rz = 0, rp = 0, ryaw = 0, rr = 0;
    const muzLen = Math.max(0.15, d ? rack.muzzleOf(wid).z : 0.4);
    const rf = rw > 0.001 ? reloadFrame(hold.reload, act.reloadU, muzLen) : null;
    if (rf) {
      // the gun comes in toward the middle, low, muzzle swung left and rolled so its working side faces
      // you; each gun's own reload pose (pitch for a break action dropping open, the jolt of a seated mag)
      // rides on top, scaled down to the close-up
      rx = (-0.01 + (rf.gun.x - 0.14) * 0.5) * rw;
      ry = (-0.06 + (rf.gun.y - 1.12) * 0.5) * rw;
      rz = (0.05 - (rf.gun.z - 0.25) * 0.3) * rw;
      rp = (0.06 + (rf.gun.pitch - 0.3) * 0.5) * rw;
      const leftFlank = hold.reload === ReloadK.Revolver || hold.reload === ReloadK.Vial;
      ryaw = ((leftFlank ? -0.85 : 1.0) - (rf.gun.yaw + 0.3) * 0.3) * rw;
      if (leftFlank) rx += 0.06 * rw;
      rr = (0.3 - (rf.gun.roll + 0.5) * 0.25) * rw;
      // long guns sit further out so the whole flank fits
      const lg = clamp01((muzLen - 0.35) / 0.4);
      rx += 0.1 * lg * rw; rz -= 0.4 * lg * rw; ry += 0.03 * lg * rw; ryaw -= 0.25 * lg * rw;
    }

    // ---- the flourish (Big Iron spin, bolt cycle rock, break-action flick)
    let fl = 0, fp = 0, fr = 0;
    if (this.flour > 0) {
      this.flour = Math.max(0, this.flour - dt / this.flourLen);
      const u = 1 - this.flour;
      if (this.flourK === 1) fr = smooth(u) * TAU; // a full spin round the barrel
      else if (this.flourK === 2) { const k = Math.sin(Math.min(1, u * 1.3) * Math.PI); fr = -0.32 * k; fp = -0.05 * k; fl = -0.012 * k; } // cant to work the bolt
      else fp = Math.sin(u * Math.PI) * 0.28; // the snap shut
    }

    // ---- charge tremble (Duel Blaster) and the Auto MG's buzz
    const ch = act.charge * act.charge;
    const buzz = act.spin * (act.shotAge < 0.15 ? 1 : 0.3);
    const jt = this.t * 61;
    const tx = (Math.sin(jt * 1.3) * 0.0016 * ch + Math.sin(jt * 2.1) * 0.0011 * buzz), tyv = (Math.sin(jt * 1.7 + 1) * 0.0014 * ch + Math.sin(jt * 2.7) * 0.0012 * buzz);

    // ---- the wall: the gun tucks up and back when the eye is right against something
    let near = 1.2;
    if (world) { world.raycast(_cam.elements[12], _cam.elements[13], _cam.elements[14], _fwd.x, _fwd.y, _fwd.z, 1.2, _hit); if (_hit.kind) near = _hit.t; }
    const tk = this.tuck.step(clamp01((0.95 - near) / 0.55), dt);

    // ---- compose the gun in camera space
    const low = 1 - r, tn = VM_TUNE;
    const sight = sightOf(wid) * tn.scale;
    const ak = d.akimbo ? 0.11 : 0;
    _p.set(
      tn.x * (d.akimbo ? 0 : 1) + (fit.x ?? 0) + ak + sx + bx + bx2 + tx + rx + low * 0.13 + rl * 0.2,
      -sight - tn.drop + by + by2 + tyv + lf + ry - low * 0.24 + fl + tk * 0.02,
      -(fit.dist + tn.far) + kz * 0.02 + rz + low * 0.1 + tk * 0.12 - ch * 0.015,
    );
    _e.set(sp + kp * 0.05 + rp - low * 0.7 + fp + tk * 0.75, tn.yaw * (d.akimbo ? 0.4 : 1) + (fit.yaw ?? 0) + sy + ky * 0.02 + ryaw + low * 0.25 - tk * 0.3, tn.roll + (fit.roll ?? 0) + rl + kr * 0.03 + rr + low * 0.55 + fr);
    _q.setFromEuler(_e).multiply(FLIP);
    _g.compose(_p, _q, _sc.setScalar(tn.scale));
    _gw.multiplyMatrices(_cam, _g);
    rack.drawAt(v.id, _gw, this.muzzle);

    // ---- the gauntlets
    const hb = this.hands[v.cls & 3];
    if (!hb.robot(_cam, this.fx)) { for (const b of this.hands) b.end(); return; }
    const twin = d.akimbo;
    hb.part(S.RPalm, _g, 0); hb.part(S.RFingers, _g, 0);
    const pull = Math.max(kick(act.shotAge, 0.12), act.charge > 0 ? 0.8 : 0);
    _m.makeRotationY(-RS * 0.55 * pull).setPosition(INDEX_KNUCKLE[0], INDEX_KNUCKLE[1], INDEX_KNUCKLE[2]);
    hb.part(S.RIndex, _m2.multiplyMatrices(_g, _m), 0);
    // the right forearm from the wrist back to the elbow under the lens
    _a.set(RS * 0.022, -0.068, -0.062).applyMatrix4(_g);
    const pistolArm = hold.pistolAim || hold.k === HoldK.SawedOff;
    _b.set((pistolArm ? 0.25 : 0.27) + ak * 0.6 + (_p.x - (fit.x ?? 0) - ak) * 0.6, pistolArm ? -0.37 : -0.36, pistolArm ? -0.06 : 0.0);
    _b.y += (_p.y + sight) * 0.6;
    this.arm(hb, S.RArm, _a, _b);

    if (twin) {
      // the off-hand gun, mirrored out to the left; it kicks on its own shots
      const own = this.akSide === 1 ? 1 : 0.25;
      _p.x -= ak * 2 + (fit.x ?? 0) * 2;
      _e.set(_e.x + kp * 0.05 * (own - 1), _e.y, -_e.z + 0.04);
      _q.setFromEuler(_e).multiply(FLIP);
      _m.compose(_p, _q, _sc);
      _m2.multiplyMatrices(_cam, _m);
      rack.drawAt(v.id, _m2, _c, true);
      hb.part(S.LTwin, _m, 0);
      _m2.makeRotationY(RS * 0.55 * pull).setPosition(-INDEX_KNUCKLE[0], INDEX_KNUCKLE[1], INDEX_KNUCKLE[2]);
      hb.part(S.LTwinIndex, _gw.multiplyMatrices(_m, _m2), 0);
      _a.set(-RS * 0.022, -0.068, -0.062).applyMatrix4(_m);
      _b.set(-0.25 - (_p.x + ak) * 0.2, -0.37, -0.06);
      this.arm(hb, S.LArm, _a, _b);
    } else {
      // the support hand: on the foregrip, cupping a one-hander, or off to the belt and back in a reload
      const two = hold.twoHand;
      let hx: number, hy: number, hz: number, onGun = true;
      if (rf) {
        if (rf.handGun) { hx = rf.hand.x; hy = rf.hand.y; hz = rf.hand.z; }
        else {
          // body frame (feet origin) to camera space, then into the gun frame for the shared smoothing
          _c.set(rf.hand.x * 0.8 - 0.08, rf.hand.y - 1.62, -rf.hand.z * 0.8);
          _m.copy(_g).invert(); _c.applyMatrix4(_m);
          hx = _c.x; hy = _c.y; hz = _c.z; onGun = false;
        }
      } else if (two) { const mz = rack.muzzleOf(wid); hx = -RS * 0.012; hy = mz.y - 0.036; hz = mz.z * hold.fore; }
      else { hx = 0; hy = 0; hz = 0; }
      if (!this.handInit) { this.hand.set(hx, hy, hz); this.handInit = true; }
      const kh = 1 - Math.exp(-dt * (rf ? 22 : 30));
      this.hand.x += (hx - this.hand.x) * kh; this.hand.y += (hy - this.hand.y) * kh; this.hand.z += (hz - this.hand.z) * kh;
      const cup = !two && !rf;
      _m.makeTranslation(this.hand.x, this.hand.y, this.hand.z);
      _m2.multiplyMatrices(_g, _m);
      if (cup) { hb.part(S.LCup, _m2, 0); hb.part(S.LCupFingers, _m2, 0); }
      else { hb.part(S.LPalm, _m2, 0); if (onGun || !rf) hb.part(S.LFingers, _m2, 0); }
      _a.set(cup ? -RS * 0.04 : -RS * 0.03, cup ? -0.07 : -0.05, cup ? -0.05 : -0.04).applyMatrix4(_m2);
      _b.set((cup ? -0.25 : -0.27) + (_p.x - (fit.x ?? 0)) * 0.4, cup ? -0.37 : -0.36, cup ? -0.07 : (two ? -0.1 : -0.02));
      _b.y += (_p.y + sight) * 0.5;
      this.arm(hb, S.LArm, _a, _b);
    }
    hb.end();
    for (const b of this.hands) if (b !== hb) b.end();
  }

  private flourLen = 0.4;
  private flourish(kind: number, len: number) { this.flourK = kind; this.flour = 1; this.flourLen = len; }

  /** a forearm from the wrist `w` back to the elbow `e` (camera space), plate up */
  private arm(hb: TypeBatch, slot: number, w: THREE.Vector3, e: THREE.Vector3) {
    _c.subVectors(e, w).normalize(); // +Z of the forearm
    _up.set(0, 1, 0);
    _rt.crossVectors(_up, _c).normalize();
    _up.crossVectors(_c, _rt);
    _m.makeBasis(_rt, _up, _c).setPosition(w);
    hb.part(slot, _m, 0);
  }
}
